// PostConsultationModal.jsx

import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  Typography,
  Chip,
} from "@mui/material";

import axios from "api/baseurl";
import { toast } from "react-toastify";

function PostConsultationModal({
  open,
  handleClose,
  patientId,
  refreshData,
}) {
  const loginData = JSON.parse(
    localStorage.getItem("loginData")
  );

  const doctorUserId = loginData?.userid;

  const [drugOptions, setDrugOptions] = useState([]);

  const drugSearchTimerRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    diagnosis: "",
    medications: [],
    instructions: "",
    followUp: "",
    decision: "",
    escalationReason: "",
  });

  // ================= FETCH MEDICINES =================

  const fetchRxNormDrugs = (query) => {
    if (!query || query.length < 2) {
      setDrugOptions([]);
      return;
    }

    if (drugSearchTimerRef.current) {
      clearTimeout(drugSearchTimerRef.current);
    }

    drugSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://rxnav.nlm.nih.gov/REST/drugs.json?name=${encodeURIComponent(
            query
          )}`
        );

        const data = await response.json();

        const groups =
          data?.drugGroup?.conceptGroup || [];

        let results = [];

        groups.forEach((group) => {
          if (
            Array.isArray(group?.conceptProperties)
          ) {
            group.conceptProperties.forEach(
              (item) => {
                if (
                  item?.name &&
                  item?.rxcui
                ) {
                  results.push({
                    label: item.name,
                    rxcui: item.rxcui,
                  });
                }
              }
            );
          }
        });

        const unique = [
          ...new Map(
            results.map((i) => [i.label, i])
          ).values(),
        ].slice(0, 50);

        setDrugOptions(unique);
      } catch (error) {
        console.error(error);
        setDrugOptions([]);
      }
    }, 400);
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const token = loginData?.token;

      const payload = {
        doctorUserId,
        patientUserId: patientId,

        diagnosis: formData.diagnosis,

        medications:
          formData.medications.map((med) =>
            typeof med === "string"
              ? med
              : med.label
          ),

        instructions: formData.instructions,

        followUp: formData.followUp,

        decision: formData.decision,
      };

      // ONLY FOR ESCALATE
      if (formData.decision === "ESCALATE") {
        payload.escalationReason =
          formData.escalationReason;
      }

      console.log("SUBMIT PAYLOAD", payload);

      await axios.post(
        "/api/clinical/decision",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(
        "Post consultation created successfully"
      );

      refreshData();

      handleClose();

      // RESET
      setFormData({
        diagnosis: "",
        medications: [],
        instructions: "",
        followUp: "",
        decision: "",
        escalationReason: "",
      });
    } catch (error) {
      console.error(error);

      const errorMessage =
    error?.response?.data?.message ||
    "Failed to submit";

  toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "22px",
        }}
      >
        Post Consultation
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={1}>
          {/* Diagnosis */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Diagnosis"
              value={formData.diagnosis}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  diagnosis: e.target.value,
                })
              }
            />
          </Grid>

          {/* MEDICATION */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={drugOptions}
              filterOptions={(x) => x}
              noOptionsText="No medication available"
              value={formData.medications}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.label
              }
              onInputChange={(e, value) => {
                fetchRxNormDrugs(value);
              }}
              onChange={(e, newValue) => {
                setFormData({
                  ...formData,
                  medications: newValue,
                });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={
                      typeof option === "string"
                        ? option
                        : option.label
                    }
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Medications"
                  placeholder="Search medicines"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Instructions */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Instructions"
              value={formData.instructions}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  instructions: e.target.value,
                })
              }
            />
          </Grid>

          {/* Follow Up */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Follow Up"
              value={formData.followUp}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  followUp: e.target.value,
                })
              }
              sx={{
  '& .MuiInputBase-root': {
    height: 47,
  },
}}
            >
              <MenuItem value="30_MIN">
                30 Minutes
              </MenuItem>

              <MenuItem value="2_HOURS">
                2 Hours
              </MenuItem>

              <MenuItem value="24_HOURS">
                24 Hours
              </MenuItem>

              <MenuItem value="NEXT_DAY">
                Next Day
              </MenuItem>
            </TextField>
            
          </Grid>

          {/* Decision */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Decision"
              value={formData.decision}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  decision: e.target.value,
                })
              }
              sx={{
  '& .MuiInputBase-root': {
    height: 47,
  },
}}
            >
              <MenuItem value="RESOLVE">
                Resolve
              </MenuItem>

              <MenuItem value="HOLD">
                Hold
              </MenuItem>

              <MenuItem value="EMERGENCY">
                Emergency
              </MenuItem>

              <MenuItem value="ESCALATE">
                Escalate
              </MenuItem>
            </TextField>
          </Grid>

          {/* ESCALATION REASON */}
          {formData.decision ===
            "ESCALATE" && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Escalation Reason"
                value={
                  formData.escalationReason
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    escalationReason:
                      e.target.value,
                  })
                }
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
        variant="contained"
 sx={{
            background:
              "linear-gradient(135deg, #5E35B1 0%, #3949AB 100%)",
            textTransform: "none",
            fontWeight: 600,color: "#fff",
          }}
        onClick={handleClose}
        
        >
            
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            background:
              "linear-gradient(135deg, #5E35B1 0%, #3949AB 100%)",
            textTransform: "none",
            fontWeight: 600,color: "#fff",
          }}
        >
          {loading
            ? "Submitting..."
            : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PostConsultationModal;